import { useEffect, useMemo, useState } from 'react';
import { ContractCoupling, ContractElection, api, type ContractDownload, type ContractField, type ContractStampEntry, type ContractValueSource } from '../lib/api';
import DealContracts from '../components/DealContracts';

/**
 * The review screen. Its entire reason to exist is the guarantee that nothing reaches a PDF
 * Joseph has not seen: every value the builder proposes is shown with its provenance, every
 * value is editable, and the blanks being left empty are listed rather than hidden.
 *
 * The screen guesses NOTHING (Task 8 review):
 * - formCode has no default. Form 64 is the Multi-Tenant Lease, 72 is a Purchase Agreement -
 *   defaulting to either would silently build the wrong instrument for a bare /contracts/<dealId>
 *   link. With no form code, the screen asks which form and renders nothing else - no field
 *   table, no generate button, and no preview fetch.
 * - representation has no default. Dual agency carries disclosure duties, and "explicit, never
 *   inferred" has to mean the operator actually chose. Nothing is fetched or generated until a
 *   side is picked.
 */

/**
 * Both calls go through `api` - `src/lib/api.ts`'s `request()` - and never through raw `fetch`.
 * This screen was the ONLY page in the frontend calling `fetch` directly, and because
 * `request()` is where the Authorization header is attached, both routes answered
 * `401 {"error":"No authorization token provided"}`: the preview showed "Could not load the
 * field list." and generate could file nothing. The shared client also owns the
 * refresh-once-then-retry path, which the raw calls missed as well.
 *
 * The row shape (`ContractField`) is IMPORTED rather than redeclared here. It mirrors
 * `ResolvedField` in backend/src/services/contractFillService.ts, and two hand-kept copies of
 * a legal document's field shape is exactly the drift this repo keeps paying for.
 */

interface Props {
  dealId: string;
  formCode: string | null;
}

const SOURCE_LABEL: Record<ContractValueSource, string> = {
  deal: 'deal', property: 'property', broker: 'broker profile', manual: 'you', unfilled: 'not filled',
};

/**
 * 🚨 THE ONLY MARK THE STAMPER WILL DRAW INTO AN AIR TICK BOX. Mirrors `CHECKBOX_MARK` in
 * backend/src/services/contractFillService.ts, and the duplication is deliberate: this screen
 * has to produce the value the stamper is known to accept, and there is no shared-types package
 * between the two halves of this repo.
 *
 * Measured against a real 9pt box: "X" is 6.003pt and draws. "Yes" is 9.498pt and "N/A" is
 * 10.002pt - both come back `refused_overflow` and print NOTHING. "✓" is worse still: pdf-lib
 * THROWS `WinAnsi cannot encode` (U+2713), so the generate 500s after the operator has already
 * reviewed and approved the page.
 *
 * Rendering these fields as a checkbox rather than a text input is what removes the class - the
 * operator can elect or not elect, and cannot type a value the document cannot carry.
 */
const CHECKBOX_MARK = 'X';

/**
 * The forms the builder has a reviewed field map for, in the order the picker lists them. The
 * code is the form's position in Joseph's AIR folder (`AIR Forms - 7:12:26/`), the same numbering
 * the backend's `src/formMaps/<code>.*.json` files use.
 */
const FORMS: { code: string; name: string }[] = [
  { code: '29', name: 'Disclosure Regarding Real Estate Agency Relationship' },
  { code: '32', name: 'Exclusive Right to Represent Owner for Sale or Lease (listing agreement)' },
  { code: '64', name: 'Multi-Tenant Lease - Net' },
  { code: '72', name: 'Purchase of Real Estate' },
];

/**
 * 🚨 A LISTING AGREEMENT HAS ONE CLIENT, SO ITS SIDE IS NOT A CHOICE. Form 32 is the contract
 * between the OWNER and us: there is no counterparty column for us to be on, and AIR's own §2.4
 * already writes the owner's dual-agency consent into the form. The screen still never guesses -
 * it shows the side rather than hiding it - but offering Lessee or Both here would only offer a
 * wrong answer. The value sent is 'lessor' because the owner is the lessor/seller in the
 * Transaction the agreement is about; nothing on form 32 is sourced from `representation`.
 */
const OWNER_ONLY_FORMS: ReadonlySet<string> = new Set(['32']);

export default function ContractBuilder({ dealId, formCode }: Props) {
  const [fields, setFields] = useState<ContractField[]>([]);
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [chosenRepresentation, setRepresentation] = useState('');
  const ownerOnly = formCode !== null && OWNER_ONLY_FORMS.has(formCode);
  const representation = ownerOnly ? 'lessor' : chosenRepresentation;
  const [error, setError] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [filed, setFiled] = useState<{ fileId: string; linkId: string; revision: string; report: ContractStampEntry[] } | null>(null);
  /**
   * The signed URL for what was just filed. `null` while it is being fetched; `'failed'` once we
   * know we cannot get one - and those are NOT the same thing, which is why this is not a
   * plain nullable. Rendering a link that 404s is worse than rendering none.
   */
  const [download, setDownload] = useState<ContractDownload | 'failed' | null>(null);
  /**
   * 🚨 HAS THE PREVIEW ACTUALLY ANSWERED? Not "is there a representation".
   *
   * `fields` starts `[]` and `unfilledRequired` is derived from it, so before the preview lands
   * - and after it FAILS - there are no fields, therefore nothing required, therefore nothing
   * blocking. Gating Generate on `unfilledRequired.length` alone meant the button went live the
   * instant a side was picked and stayed live through a 500, and a click then posts
   * `overrides: {}` and files a contract stamped entirely from database values the operator
   * never saw. Emptiness is not approval.
   */
  const [previewLoaded, setPreviewLoaded] = useState(false);
  /**
   * The form's "check one" groups, side-filtered by the server. Held separately from `fields`
   * because they describe a relationship BETWEEN boxes, which no per-field flag can carry.
   */
  const [elections, setElections] = useState<ContractElection[]>([]);
  /** Value-implies-tick rules. Held with the elections, and re-evaluated the same way. */
  const [couplings, setCouplings] = useState<ContractCoupling[]>([]);

  useEffect(() => {
    // Withdraw Generate the moment the inputs change. Until the NEW answer lands, whatever is
    // on screen belongs to the previous form or the previous side, and generating from it would
    // file that one.
    setPreviewLoaded(false);
    // Guard: nothing is fetched until BOTH a form and a representation are known. Either one
    // missing means the screen would otherwise be showing (or generating from) a guess.
    if (!formCode || !representation) return;
    let cancelled = false;
    (async () => {
      try {
        // No `revision`: the backend resolves the map by formCode when none is given, and an
        // empty string is not "no revision" - it 500s every preview.
        const { data } = await api.previewContract({ formCode, dealId, representation });
        // Cancelled-closure guard. Changing the representation re-runs this effect while the
        // previous request is still in the air; without it a stale answer for the OLD side can
        // land last and repaint the table with the wrong side's fields.
        // Both together: the table and the permission to act on it arrive at the same moment.
        if (!cancelled) {
          setFields(data.fields); setElections(data.elections);
          setCouplings(data.couplings); setPreviewLoaded(true);
        }
      } catch {
        if (!cancelled) setError('Could not load the field list.');
      }
    })();
    return () => { cancelled = true; };
  }, [dealId, formCode, representation]);

  /**
   * Resolve the filed contract to something the operator can actually open.
   *
   * Fetched here rather than on a button click so the result is a REAL anchor - clickable,
   * copyable, right-clickable - instead of a `window.open` after an await, which popup blockers
   * eat. One extra request, and only ever after a successful generate.
   */
  useEffect(() => {
    if (!filed) return;
    let cancelled = false;
    setDownload(null);
    (async () => {
      try {
        const { data } = await api.downloadContract(filed.linkId);
        if (!cancelled) setDownload(data);
      } catch {
        // The filing itself still happened and is still reported. Only the link is withheld.
        if (!cancelled) setDownload('failed');
      }
    })();
    return () => { cancelled = true; };
  }, [filed]);

  const valueOf = (f: ContractField) => edits[f.fieldId] ?? f.value;

  /**
   * 🚨 DOCUMENT ORDER, so the table reads the way the page does.
   *
   * The map lists blanks in DETECTION order, which on the purchase agreement's page 11 runs
   * y 646, 509, 138, 127, 497, 279, 267, 634. Rendered raw, the eight rows all labelled
   * `Address:` appear in an order with no relation to the paper, and the operator has no way to
   * tell which is which. `.slice()` first - `sort` mutates, and the array belongs to the
   * response, not to the view.
   */
  const rows = useMemo(() => [...fields].sort((a, b) => a.order - b.order), [fields]);

  // Field objects, not just ids: the blocking count comes from .length either way, but the
  // on-screen message names fields by LABEL. Reusing the fieldId here would print e.g. "county"
  // right next to the "Left blank" list's own "county" text node, and getByText(/county/) (or
  // any human skimming the two messages) cannot tell them apart - two elements would carry the
  // identical literal substring for two different reasons (one says "not shown to the operator
  // at all", the other says "required and still blank"). Labels keep the messages distinguishable.
  const unfilledRequired = useMemo(
    () => fields.filter((f) => f.required && valueOf(f) === ''),
    [fields, edits],
  );
  const unfilled = useMemo(
    () => fields.filter((f) => valueOf(f) === '').map((f) => f.fieldId),
    [fields, edits],
  );

  /**
   * 🚨 ELECTIONS ARE RE-EVALUATED HERE, LIVE, and not taken from the preview's own verdict.
   *
   * The preview is fetched WITHOUT overrides and only when the deal, form or representation
   * changes, so the server's `brokenElections` describes the un-edited document. The moment the
   * operator ticks a box that verdict is stale - and the two failures this exists to catch are
   * both created BY editing: clearing the standing "is not attached" default without ticking the
   * other, or ticking the other without clearing the default.
   *
   * The one rule owned here is "exactly one tick". Which boxes are in play was decided by the
   * server (side-suppression is policy). The backend gate on generate is still the authority;
   * this only stops the operator reaching a 400 they cannot read.
   */
  const brokenElections = useMemo(
    () => elections.filter((e) => {
      const ticked = e.fields.filter((id) => {
        const field = fields.find((f) => f.fieldId === id);
        return field !== undefined && valueOf(field) !== '';
      });
      return ticked.length !== 1;
    }),
    [elections, fields, edits],
  );

  /**
   * 🚨 A VALUE WITHOUT THE TICK THAT GIVES IT EFFECT. Re-evaluated live for the same reason as the
   * elections above: the preview is fetched without overrides, so the server's verdict describes
   * the un-edited document, and this rule is triggered BY editing - typing an addendum paragraph
   * range is exactly what makes the box mandatory.
   *
   * The one rule owned here is "if any trigger has a value, the target must be marked". Which
   * fields those are was decided by the server.
   */
  const unmetCouplings = useMemo(
    () => couplings.filter((c) => {
      const valueFor = (id: string) => {
        const field = fields.find((f) => f.fieldId === id);
        return field === undefined ? '' : valueOf(field);
      };
      return c.whenFilled.some((id) => valueFor(id) !== '') && valueFor(c.thenTicked) === '';
    }),
    [couplings, fields, edits],
  );

  /**
   * 🚨 WHAT THE STAMPER REFUSED TO DRAW. The last hop of a chain that was already complete:
   * `stampForm` records `refused_overflow` rather than overrun a blank, the controller returns
   * the report in the 201 body, `api.ts` validates it - and this page used to drop it, so a
   * contract with a silently empty blank was announced as "Contract filed".
   *
   * By LABEL, because `field_009` does not tell the operator which blank came back empty. The
   * fieldId is the fallback only for the impossible case of a report entry with no matching row.
   */
  const refused = useMemo(() => {
    if (!filed) return [];
    const labelOf = new Map(fields.map((f) => [f.fieldId, f.label]));
    return filed.report
      .filter((e) => e.status === 'refused_overflow')
      .map((e) => labelOf.get(e.fieldId) ?? e.fieldId);
  }, [filed, fields]);

  const generate = async () => {
    // In-flight guard: without it an operator unsure whether the first click landed can
    // double-click and file TWO contracts against the deal.
    if (generating) return;
    // Unreachable in practice - the button only renders past the `!formCode` early return
    // below - but the compiler cannot see that, and the alternative is a non-null assertion,
    // which is a guess with a nicer syntax. The screen must never post without a form.
    if (!formCode) return;
    // Clear the stale error, so a retry that succeeds does not sit under the previous failure.
    setGenerating(true);
    setError(null);
    try {
      const { data } = await api.generateContract({ formCode, dealId, representation, overrides: edits });
      // Confirm what was filed. Without this the operator cannot tell a success from a no-op.
      setFiled({ fileId: data.fileId, linkId: data.linkId, revision: data.revision, report: data.report });
    } catch (err) {
      // Three different failures, three different truths, and telling them apart matters here:
      //
      //  - The SERVER refused: `request()` throws APIError carrying its message, which names
      //    the unfilled required fields. Shown verbatim - that text is the operator's
      //    instruction, and replacing it with a generic one throws the instruction away.
      //  - The server was never reached: `fetch` rejects with a TypeError. Nothing was
      //    generated, and saying so is both true and reassuring.
      //  - The server answered a shape `api.ts` does not recognise: that refusal must NOT
      //    claim "nothing was generated", because the request may well have filed a contract.
      //    Its own message is shown instead.
      setError(
        err instanceof TypeError
          ? 'Could not reach the server. Nothing was generated.'
          : err instanceof Error
            ? err.message
            : 'Generation failed.',
      );
    } finally {
      setGenerating(false);
    }
  };

  // No form code: ask which one, render nothing else. No field table, no generate button, and
  // (per the effect's guard above) no preview fetch either.
  if (!formCode) {
    return (
      <div className="p-6">
        <h1 className="font-display text-[28px] font-semibold uppercase leading-[1.06] tracking-[-.01em] text-gray-900 sm:text-[32px]">Which form?</h1>
        <p>Choose the AIR CRE form to build.</p>
        <ul>
          {FORMS.map((f) => (
            <li key={f.code}>
              <a className="text-primary-500 hover:underline" href={`/contracts/${dealId}?formCode=${f.code}`}>
                {f.code} - {f.name}
              </a>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  return (
    <div className="p-6">
      <h1 className="font-display text-[28px] font-semibold uppercase leading-[1.06] tracking-[-.01em] text-gray-900 sm:text-[32px]">AIR form {formCode}</h1>

      {/*
        Above the form picker on purpose: "what have I already sent on this deal" is a question
        asked BEFORE choosing a form. `refreshKey` is the just-filed contract, so one generated
        in this session joins the list instead of only appearing on the next visit.
      */}
      <DealContracts dealId={dealId} refreshKey={filed?.fileId} only="contracts" />

      {error && <p role="alert">{error}</p>}
      {filed && (
        <>
          <p role="status">Contract filed (revision {filed.revision}).</p>
          {download && download !== 'failed' && (
            <p>
              <a href={download.url} target="_blank" rel="noreferrer">
                Open the filed contract ({download.filename})
              </a>
            </p>
          )}
          {download === 'failed' && (
            <p data-testid="download-unavailable">
              The contract was filed, but this screen could not get a link to it. Open it from the
              deal's documents.
            </p>
          )}
          {refused.length > 0 && (
            <p data-testid="refused-list" role="alert">
              These did not print - the value was wider than the blank and the stamper refused
              rather than overrun it: {refused.join(', ')}. Shorten each one and generate again.
            </p>
          )}
        </>
      )}

      {ownerOnly ? (
        <p data-testid="owner-only">We represent: the Owner (a listing agreement is always the owner's).</p>
      ) : (
        <>
          <label htmlFor="representation">We represent</label>
          <select
            id="representation"
            value={chosenRepresentation}
            onChange={(e) => setRepresentation(e.target.value)}
          >
            <option value="">Choose…</option>
            <option value="lessor">Lessor</option>
            <option value="lessee">Lessee</option>
            <option value="both">Both</option>
          </select>
        </>
      )}

      {representation && (
        <>
          <table>
            <thead>
              <tr><th>Field</th><th>Where</th><th>Value</th><th>From</th></tr>
            </thead>
            <tbody>
              {rows.map((f) => (
                <tr key={f.fieldId}>
                  <td><label htmlFor={f.fieldId}>{f.label}</label></td>
                  {/*
                    Where the blank actually sits. `side` is printed only when the map declares
                    one - a blank that is not column-specific must not be given an owner, because
                    position is not ownership.
                  */}
                  <td data-testid={`where-${f.fieldId}`}>
                    p{f.page} · {f.column}{f.side ? ` · ${f.side}` : ''}
                  </td>
                  <td>
                    {f.type === 'checkbox' ? (
                      <input
                        id={f.fieldId}
                        type="checkbox"
                        checked={valueOf(f) === CHECKBOX_MARK}
                        onChange={(e) =>
                          setEdits((prev) => ({
                            ...prev,
                            // '' is an explicit override meaning "deliberately not elected", which
                            // `resolveFields` honours instead of re-reading the database value.
                            [f.fieldId]: e.target.checked ? CHECKBOX_MARK : '',
                          }))
                        }
                      />
                    ) : (
                      <input
                        id={f.fieldId}
                        value={valueOf(f)}
                        onChange={(e) => setEdits((prev) => ({ ...prev, [f.fieldId]: e.target.value }))}
                      />
                    )}
                  </td>
                  <td>{SOURCE_LABEL[edits[f.fieldId] !== undefined ? 'manual' : f.source]}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <section>
            <h2>Left blank</h2>
            <p data-testid="unfilled-list">{unfilled.join(', ') || 'nothing'}</p>
          </section>

          <button
            type="button"
            onClick={generate}
            disabled={!previewLoaded || unfilledRequired.length > 0 || brokenElections.length > 0
              || unmetCouplings.length > 0 || generating}
          >
            {generating ? 'Generating…' : 'Generate'}
          </button>
          {unfilledRequired.length > 0 && (
            <p>Required and still empty: {unfilledRequired.map((f) => f.label).join(', ')}</p>
          )}
          {brokenElections.length > 0 && (
            <p data-testid="broken-elections">
              Check one: {brokenElections.map((e) => e.label).join('; ')}
            </p>
          )}
          {unmetCouplings.length > 0 && (
            <p data-testid="unmet-couplings">
              Needs its box ticked: {unmetCouplings.map((c) => c.label).join('; ')}
            </p>
          )}
        </>
      )}
    </div>
  );
}
